import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import swtAlert from "lib/sweetAlert";
import {
  getAuthUserEmail,
  submitInvestmentCancellationRequest,
} from "lib/investmentCancellation";
import { WITHDRAWAL_DEADLINE_PHRASE } from "lib/ni45110Copy";
import { formatMoney, getProjectTitle } from "lib/investmentListHelpers";
import { selectAuthUser } from "store/reducer";

/**
 * Reusable cancel-investment flow: SweetAlert confirm → modal → API → refresh.
 * @param {{ onSuccess?: () => void }} [options]
 */
export function useInvestmentCancellation({ onSuccess } = {}) {
  const user = useSelector(selectAuthUser);
  const investorEmail = getAuthUserEmail(user);

  const [modalOpen, setModalOpen] = useState(false);
  const [investment, setInvestment] = useState(null);
  const [cancellationNotes, setCancellationNotes] = useState("");
  const [notesError, setNotesError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setInvestment(null);
    setCancellationNotes("");
    setNotesError("");
  }, []);

  const beginCancellation = useCallback(
    async (inv) => {
      if (submitting) return;

      const title = getProjectTitle(inv);
      const amount = inv?.amount ?? inv?.currentValue ?? 0;
      const currency = inv?.currency ?? "USD";
      const amountLabel = formatMoney(amount, currency);
      const result = await swtAlert.warning(
        "Cancel investment?",
        `Request cancellation for "${title}" (${amountLabel})? Withdrawal must be ${WITHDRAWAL_DEADLINE_PHRASE}. You can provide a reason in the next step.`,
        "Yes, continue",
        "No"
      );

      if (!result?.isConfirmed) return;

      swtAlert.close();
      setInvestment(inv);
      setCancellationNotes("");
      setNotesError("");
      setModalOpen(true);
    },
    [submitting]
  );

  const submitCancellation = useCallback(async () => {
    if (submitting || !investment) return;

    const trimmed = cancellationNotes.trim();
    if (!trimmed) {
      setNotesError("Cancellation reason is required.");
      return;
    }

    setNotesError("");
    setSubmitting(true);

    try {
      const outcome = await submitInvestmentCancellationRequest(investment, {
        notes: trimmed,
      });

      if (!outcome.ok) {
        closeModal();
        await swtAlert.error("Cancellation failed", outcome.message);
        return;
      }

      closeModal();
      await swtAlert.success("Cancellation requested", outcome.message);
      onSuccess?.();
    } catch (e) {
      closeModal();
      await swtAlert.error(
        "Cancellation failed",
        e?.message || "Could not submit cancellation request."
      );
    } finally {
      setSubmitting(false);
    }
  }, [submitting, investment, cancellationNotes, closeModal, onSuccess]);

  const handleNotesChange = useCallback((value) => {
    setCancellationNotes(value);
    if (value.trim()) setNotesError("");
  }, []);

  return {
    modalOpen,
    setModalOpen,
    investment,
    cancellationNotes,
    notesError,
    submitting,
    investorEmail,
    beginCancellation,
    submitCancellation,
    closeModal,
    handleNotesChange,
  };
}
